'use client';

import { useFormatter, useTranslations } from 'next-intl';
import { type ReactNode, useMemo } from 'react';
import { useFuturesSummary } from '@/lib/api/futures/hooks';
import type { Coin } from '@/lib/api/transaction/models';
import { pnlToneClass } from '@/lib/utils/calculatePnl';
import { formatMasked, formatMaskedMoney } from '@/lib/utils/formatAmount';
import { SummaryRow } from './SummaryRow';

interface CryptoTradingSummaryProps {
  /** Coins with their USD rates, to bring every settle coin to dollars. */
  coins: Coin[];
  hidden: boolean;
  renderAccountLink: (href: string, label: ReactNode) => ReactNode;
}

const DEALS_HREF = '/trade/deals';
const APPROX_USD_FORMAT = { prefix: '≈$', decimals: 2 };
// formatMoney signs before the prefix: PnL drops the "≈" rather than read "-≈$5.00".
const PNL_USD_FORMAT = { prefix: '$', decimals: 2 };

/** The Crypto Trading tab (Figma 15073:84607): futures positions, orders, margin and uPnL. */
export function CryptoTradingSummary({
  coins,
  hidden,
  renderAccountLink,
}: CryptoTradingSummaryProps) {
  const { data: summary } = useFuturesSummary();
  const t = useTranslations('balancePopover');
  const format = useFormatter();

  // Margin and uPnL settle in several coins (USDT, USDC, the base of inverse pairs).
  const totalsUsd = useMemo(() => {
    if (!summary) return undefined;
    let marginUsd = 0;
    let pnlUsd = 0;
    for (const [symbol, totals] of Object.entries(summary.bySettleCoin)) {
      const rate = coins.find((c) => c.shortName.toUpperCase() === symbol)?.usdRate;
      // An unpriced coin leaves the total unknown — a partial sum would pass for the real one.
      if (rate == null) return undefined;
      marginUsd += totals.margin * rate;
      pnlUsd += totals.unrealizedPnl * rate;
    }
    return { marginUsd, pnlUsd };
  }, [summary, coins]);

  return (
    <div className="flex flex-col gap-1">
      {renderAccountLink(
        DEALS_HREF,
        <SummaryRow
          label={t('openPosition')}
          value={formatMasked(summary?.openPositions, hidden, format.number)}
          interactive
        />,
      )}
      {renderAccountLink(
        DEALS_HREF,
        <SummaryRow
          label={t('openOrders')}
          value={formatMasked(summary?.activeOrders, hidden, format.number)}
          interactive
        />,
      )}
      <SummaryRow
        label={t('marginUsed')}
        value={formatMaskedMoney(totalsUsd?.marginUsd, hidden, APPROX_USD_FORMAT)}
      />
      <SummaryRow
        label={t('unrealizedPnl')}
        value={formatMaskedMoney(totalsUsd?.pnlUsd, hidden, PNL_USD_FORMAT)}
        valueClassName={pnlToneClass(totalsUsd?.pnlUsd)}
      />
    </div>
  );
}
